import clsx from 'clsx';
import { type CSSProperties, type KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AiOutlineWarning } from 'react-icons/ai';
import { BiCheck, BiSolidChevronDown } from 'react-icons/bi';

import { useClickOutside } from '@/hooks/useClickOutside';

import s from './inputs.module.scss';

/** Where the portalled list sits: under the field, or over it when there's no room below. */
const popoutPlacement = (anchor: HTMLElement | null): CSSProperties | undefined => {
  const rect = anchor?.getBoundingClientRect();
  if (!rect) return;
  const maxHeight = 200;
  const keyboardInset =
    Number.parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--keyboard-inset'),
    ) || 0;
  const viewportBottom = (window.visualViewport?.height ?? window.innerHeight) - keyboardInset;
  const spaceBelow = viewportBottom - rect.bottom;
  const up = spaceBelow < maxHeight && rect.top > spaceBelow;
  return {
    position: 'fixed',
    left: rect.left,
    width: rect.width,
    right: 'auto',
    zIndex: 10001,
    ...(up ? { bottom: window.innerHeight - rect.top + 4, top: 'auto' } : { top: rect.bottom + 4 }),
  };
};

export const Select = <T extends null | number | string>({
  label,
  error,
  placeholder,
  options,
  disabled,
  mandatory,
  className,
  warning,
  ...props
}: {
  label: string;
  error?: string;
  /** A heads-up under the field, shown while there is no error: valid, but worth knowing. */
  warning?: string;
  options: { label: React.ReactNode; value: T; disabled?: boolean }[];
  placeholder?: string;
  disabled?: boolean;
  mandatory?: boolean;
  className?: string;
} & (
  | {
      value: T;
      onChange(v: T): void;
    }
  | {
      values: T[];
      onMultipleChange(v: T[]): void;
    }
)) => {
  const [opened, setIsOpened] = useState(false);
  const popoutRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [popoutStyle, setPopoutStyle] = useState<CSSProperties>({});
  // The option the arrow keys are on. Focus stays on the field and the list says which
  // option is meant through aria-activedescendant, so the portalled list needs no focus.
  const [activeIndex, setActiveIndex] = useState(-1);
  const listId = useId();

  const ref = useClickOutside<HTMLLabelElement>((event) => {
    if (popoutRef.current?.contains(event.target as Node)) return;
    setIsOpened(false);
  });

  useEffect(() => {
    if (!opened) return;
    const place = () => {
      const style = popoutPlacement(anchorRef.current);
      if (style) setPopoutStyle(style);
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    window.visualViewport?.addEventListener('resize', place);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      window.visualViewport?.removeEventListener('resize', place);
    };
  }, [opened]);

  const isMultiple = 'values' in props;

  const selectedOptionLabel = isMultiple
    ? options
        .filter(({ value }) => props.values.includes(value))
        .map(({ label }) => label)
        .join(', ')
    : (options.find((o) => o.value === props.value)?.label ?? '');

  const isSelected = (v: T) => (isMultiple ? props.values.includes(v) : props.value === v);

  const step = (from: number, by: 1 | -1) => {
    for (let i = from + by; i >= 0 && i < options.length; i += by) {
      if (!options[i].disabled) return i;
    }
    return from;
  };

  const open = () => {
    // Placed before the list first renders, not after. Unplaced, it lands in the
    // body's flow below the fold, and the active option's scrollIntoView then
    // scrolls the whole page to reach it — the page behind a modal jumps.
    const style = popoutPlacement(anchorRef.current);
    if (style) setPopoutStyle(style);
    setIsOpened(true);
    const selected = options.findIndex((o) => isSelected(o.value));
    setActiveIndex(selected >= 0 ? selected : step(-1, 1));
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (disabled) return;
    if (!opened) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
        e.preventDefault();
        open();
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => step(i, e.key === 'ArrowDown' ? 1 : -1));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (options[activeIndex])
          handleChange(options[activeIndex].value, options[activeIndex].disabled);
        if (!isMultiple) setIsOpened(false);
        break;
      case 'Escape':
        // Closes the list only: the modal around the field listens for Esc on document.
        e.preventDefault();
        e.stopPropagation();
        setIsOpened(false);
        break;
      case 'Tab':
        setIsOpened(false);
        break;
    }
  };

  useEffect(() => {
    if (opened && activeIndex >= 0) {
      document.getElementById(`${listId}-${activeIndex}`)?.scrollIntoView({ block: 'nearest' });
    }
  }, [opened, activeIndex, listId]);

  const handleOpen = () => {
    if (!disabled) {
      if (isMultiple || !opened) {
        open();
      } else {
        setIsOpened(false);
      }
    }
  };

  const handleChange = (v: T, optionDisabled?: boolean) => {
    if (optionDisabled) {
      return;
    }
    if (isMultiple) {
      const wasSelected = props.values.includes(v);
      const nextValues = wasSelected
        ? props.values.filter((value) => value !== v)
        : [...props.values, v];
      props.onMultipleChange(nextValues);
    } else {
      props.onChange(v);
    }
  };

  return (
    <label className={clsx(s.InputRoot, className)} onClick={handleOpen} ref={ref}>
      <div className={s.Label}>
        <span>{label}</span>
        {mandatory && <span>*</span>}
      </div>
      <div
        ref={anchorRef}
        role="combobox"
        tabIndex={disabled ? -1 : 0}
        aria-label={label}
        aria-expanded={opened}
        aria-haspopup="listbox"
        aria-controls={opened ? listId : undefined}
        aria-activedescendant={opened && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
        aria-disabled={disabled || undefined}
        aria-invalid={!!error || undefined}
        onKeyDown={handleKeyDown}
        className={clsx(
          s.InputWrapper,
          disabled && s.disabled,
          s.SelectWrapper,
          error && s.error,
          opened && s.Opened,
          s.withIcon,
        )}
      >
        <div
          className={clsx('body-medium', s.Input)}
          // The value is clamped to one line, so hovering is the only way back to the
          // full list once the selection outgrows the field.
          title={typeof selectedOptionLabel === 'string' ? selectedOptionLabel : undefined}
        >
          {selectedOptionLabel || <span className={s.Placeholder}>{placeholder}</span>}
        </div>
        <BiSolidChevronDown className={s.icon} />
        {opened &&
          createPortal(
            <div ref={popoutRef} className={s.Popout} style={popoutStyle} data-floating-child="">
              <ul
                id={listId}
                role="listbox"
                aria-multiselectable={isMultiple || undefined}
                className={s.PopoutScroll}
              >
                {options.map(({ label, value, disabled: optionDisabled }, index) => (
                  <li
                    key={value}
                    id={`${listId}-${index}`}
                    role="option"
                    aria-selected={isSelected(value)}
                    aria-disabled={optionDisabled || undefined}
                    className={clsx(
                      optionDisabled && s.optionDisabled,
                      index === activeIndex && s.optionActive,
                    )}
                    onClick={() => handleChange(value, optionDisabled)}
                  >
                    {isMultiple && (
                      <div
                        className={clsx(
                          s.multipleCheckbox,
                          props.values.includes(value) && s.multipleCheckboxOn,
                        )}
                      >
                        {props.values.includes(value) && <BiCheck size={18} />}
                      </div>
                    )}{' '}
                    {label}
                  </li>
                ))}
              </ul>
            </div>,
            document.body,
          )}
      </div>
      {!!error && <p className={s.Error}>{error}</p>}
      {!error && !!warning && (
        <p className={s.Warning}>
          <AiOutlineWarning size={14} aria-hidden="true" />
          {warning}
        </p>
      )}
    </label>
  );
};
